/**
 * HTTP-клиент API.
 *
 * Две вещи, ради которых он существует:
 *  1. Прозрачное обновление access-токена. При 401 запрос ставится в очередь,
 *     выполняется один общий refresh, и запросы повторяются — иначе десять
 *     параллельных запросов дашборда выполнили бы десять ротаций подряд и
 *     сервер счёл бы это повторным использованием токена.
 *  2. Единый разбор ошибок: сервер отвечает {error:{code,message,details}}.
 */

import type { Tokens } from './types'

const BASE = import.meta.env.VITE_API_BASE ?? ''
const PREFIX = `${BASE}/api/v1`
const STORAGE_KEY = 'eam.session'

export interface Session {
  accessToken: string
  refreshToken: string
  tenant: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message)
  }

  /** Требуется второй фактор — не ошибка ввода, а следующий шаг входа. */
  get isMfaRequired(): boolean {
    return this.code === 'mfa_required'
  }
}

let session: Session | null = loadSession()
let refreshing: Promise<boolean> | null = null
const listeners = new Set<(s: Session | null) => void>()

function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

export function getSession(): Session | null {
  return session
}

export function setSession(next: Session | null): void {
  session = next
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* приватный режим браузера — работаем без сохранения между вкладками */
  }
  listeners.forEach((listener) => listener(next))
}

export function onSessionChange(listener: (s: Session | null) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'http_error'
  let message = `HTTP ${response.status}`
  let details: Record<string, unknown> | undefined
  try {
    const body = await response.json()
    if (body?.error) {
      code = body.error.code ?? code
      message = body.error.message ?? message
      details = body.error.details
    }
  } catch {
    /* тело не JSON — оставляем общее сообщение */
  }
  return new ApiError(response.status, code, message, details)
}

async function refreshTokens(): Promise<boolean> {
  if (!session?.refreshToken) return false
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const response = await fetch(`${PREFIX}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: session!.refreshToken }),
        })
        if (!response.ok) {
          setSession(null)
          return false
        }
        const tokens = (await response.json()) as Tokens
        setSession({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          tenant: session!.tenant,
        })
        return true
      } catch {
        return false
      } finally {
        refreshing = null
      }
    })()
  }
  return refreshing
}

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | string[]>
  retryOnUnauthorized?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${PREFIX}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === '') continue
    if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)))
    else url.searchParams.set(key, String(value))
  }
  return url.toString()
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, retryOnUnauthorized = true } = options

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 401 && retryOnUnauthorized && session?.refreshToken) {
    if (await refreshTokens()) {
      return request<T>(path, { ...options, retryOnUnauthorized: false })
    }
  }

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** URL realtime-подписки: токен идёт параметром, заголовки в WS недоступны. */
export function streamUrl(): string | null {
  if (!session?.accessToken) return null
  const httpBase = BASE || window.location.origin
  const url = new URL('/ws/incidents', httpBase)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.searchParams.set('token', session.accessToken)
  return url.toString()
}
